import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'react-bootstrap'
import CalcSection from './CalcSection'
import CalcParagraph from './CalcParagraph'
import NavBtn from './NavBtn'
import NavBar from './NavBar'
import ALink from './ALink'
import * as formConstants from '../forms/form'
import FormattedConnectedInput from '../forms/FormattedConnectedInput'
import PopOver from './PopOver'
import DynamicGrowForm from '../forms/DynamicGrowForm'
import HelpFilter from './HelpFilter'

export const otherIncomeSectionId = 7

/**
 * Basic component for rendering Section 7 of the calculator
 */
export default class Section7 extends Component {
  /**
   * Render component
   */
  render () {
    return (
      <CalcSection title={this.props.title}>
        <CalcParagraph>
          <HelpFilter showLevel={[1, 2]}>
            We started the previous section by stating that there are two
            fundamental ways to cover monthly costs in retirement: to have a
            source of income and to use up assets.
          </HelpFilter>{' '}
          Having captured the assets portion of the equation, we'll tackle
          income in this section.
        </CalcParagraph>
        <HelpFilter showLevel={[1, 2]}>
          <CalcParagraph>
            The two most common sources of income in retirement are pensions and
            Social Security. Technically Social Security is a pension but many
            people think of pensions as being provided by an employer or union
            after many years. The technical distinction isn't important but
            certainly most Americans will be eligible to receive Social Security
            where a much smaller percent of people will rely on an employer
            provided pension in retirement.
          </CalcParagraph>
          <CalcParagraph>
            <HelpFilter showLevel={[1]}>
              Popular media is constantly feeding the public stories that Social
              Security is "running out" and essentially that anyone under the
              age of forty shouldn't plan on receiving any Social Security
              benefits. This just isn't true. The Social Security Administration
              releases
              <ALink href='https://www.ssa.gov/agency/budget-and-performance.html'>
                detailed reports
              </ALink>
              of the Social Security financial situation that can be reviewed by
              anyone. And people do. The fact is that
              <ALink href='https://blog.ssa.gov/social-security-funded-until-2034-and-about-three-quarters-funded-for-the-long-term-many-options-to-address-the-long-term-shortfall/'>
                Social Security is fully funded through 2034 and three-quarters
                funded thereafter.
              </ALink>
              That means if nothing changed about Social Security from here into
              the foreseeable future, if you retire after 2034 you could count
              on receiving about seventy-five cents on the dollar for what you
              have paid into the system. Of course,
              <ALink href='https://www.forbes.com/sites/timworstall/2016/07/17/its-not-that-social-security-will-run-out-of-cash-its-whether-taxes-will-rise-to-fund-it/#32a4ae696324'>
                things probably will change
              </ALink>
              because Social Security is an extremely popular program and the
              odds are that it will be fully funded well into the retirement
              years of everyone over the age of 18 today. But that is just an
              opinion. If you are not comfortable relying on Social Security for
              retirement, that is a reasonable position. Either way, enter the
              amount of Social Security income you estimate you will receive in
              retirement, per month, below.
            </HelpFilter>
            If you would like a government provided estimate of what your
            benefit may be in retirement, there is
            <ALink href='https://www.ssa.gov/retire/estimator.html'>
              a decent estimate calculator here.
            </ALink>
          </CalcParagraph>
        </HelpFilter>
        <Form style={formConstants.formWrapperStyle} horizontal>
          <FormattedConnectedInput
            label={formConstants.ssIncomeLabel}
            reduxKey={formConstants.ssIncomeKey}
            dollarForm
          />
        </Form>
        <HelpFilter showLevel={[1]}>
          <CalcParagraph>
            While the number of people receiving employer provided pensions has
            slowly declined over the last thirty years, it is still what many
            people will rely on for a primary income source in retirement. If
            you count yourself among those, enter your estimated monthly
            distribution below. If you aren't sure what it will be, contact your
            plan administrator to find out or take an educated guess.
          </CalcParagraph>
        </HelpFilter>
        <Form style={formConstants.formWrapperStyle} horizontal>
          <FormattedConnectedInput
            label={formConstants.pensionIncomeLabel}
            reduxKey={formConstants.pensionIncomeKey}
            dollarForm
          />
        </Form>
        <CalcParagraph>
          Many people may have other sources of income to rely on in retirement.
          For example, if you own a rental property, you may rely on rent income
          or residual income from work done before retirement or you may simply
          plan on supplementing your income with a part time job. Below, enter
          any form of income that is not covered by Social Security or a pension
          but will help cover costs in retirement. Enter the amount(s) as{' '}
          <PopOver linkText='net income'>
            The income you receive after taxes or any other costs have been
            subtracted.
          </PopOver>.{' '}
        </CalcParagraph>
        <DynamicGrowForm
          reduxFormLinesKey='section7FormLines'
          sectionNumber={otherIncomeSectionId}
        />
        <NavBar>
          <NavBtn nextSection={6} back />
          <NavBtn nextSection={8} />
        </NavBar>
      </CalcSection>
    )
  }
}

CalcSection.propTypes = {
  title: PropTypes.string.isRequired
}



// WEBPACK FOOTER //
// ./src/components/sections/Section7Income.js